package br.com.htms.inspeccaocampo.data

object RemJson {
    fun payloadToJson(p: RemMonthPayload): String = buildString {
        append('{')
        fun num(key: String, v: Double?) {
            if (v != null) append("\"$key\":$v,")
        }
        num("numeroColaboradores", p.numeroColaboradores)
        num("hher", p.hher)
        num("homemHoraTreinamento", p.homemHoraTreinamento)
        num("horaAbsenteismoMedico", p.horaAbsenteismoMedico)
        num("numeroCat", p.numeroCat)
        append("\"ocorrenciasComLesao\":")
        append(breakdownToJson(p.ocorrenciasComLesao))
        append(',')
        append("\"ocorrenciasTrajeto\":")
        append(breakdownToJson(p.ocorrenciasTrajeto))
        append(',')
        num("danosPropriedade", p.danosPropriedade)
        val blocs = p.empresasBlocosNormalizados()
        val desc = blocs.map { it.nome.trim() }.filter { it.isNotEmpty() }.joinToString("; ")
            .ifBlank { p.empresasParaExcel() }
        append("\"descricaoEmpresas\":")
        append(jsonStr(desc))
        append(',')
        append("\"empresas\":")
        append(stringListToJson(blocs.map { it.nome }.ifEmpty { listOf("") }))
        append(',')
        append("\"nomesColaboradores\":")
        append(stringListToJson(nomesDe(p.ausencias, p.nomesColaboradores)))
        append(',')
        append("\"ausencias\":")
        append(ausenciasToJson(p.ausencias))
        append(',')
        append("\"empresasBlocos\":")
        append('[')
        blocs.forEachIndexed { i, b ->
            if (i > 0) append(',')
            append(empresaBlocoToJson(b))
        }
        append(']')
        append('}')
    }

    private fun empresaBlocoToJson(b: RemEmpresaBloco): String = buildString {
        append('{')
        append("\"nome\":"); append(jsonStr(b.nome)); append(',')
        fun num(key: String, v: Double?) {
            if (v != null) append("\"$key\":$v,")
        }
        num("numeroColaboradores", b.numeroColaboradores)
        num("hher", b.hher)
        num("homemHoraTreinamento", b.homemHoraTreinamento)
        num("horaAbsenteismoMedico", b.horaAbsenteismoMedico)
        num("numeroCat", b.numeroCat)
        append("\"ocorrenciasComLesao\":")
        append(breakdownToJson(b.ocorrenciasComLesao))
        append(',')
        append("\"ocorrenciasTrajeto\":")
        append(breakdownToJson(b.ocorrenciasTrajeto))
        append(',')
        num("danosPropriedade", b.danosPropriedade)
        append("\"nomesColaboradores\":")
        append(stringListToJson(nomesDe(b.ausencias, b.nomesColaboradores)))
        append(',')
        append("\"ausencias\":")
        append(ausenciasToJson(b.ausencias))
        append('}')
    }

    private fun nomesDe(ausencias: List<RemAusencia>, legado: List<String>): List<String> {
        val nomes = ausencias.map { it.nome }
        return (if (nomes.any { it.isNotBlank() }) nomes else legado).ifEmpty { listOf("") }
    }

    private fun ausenciasToJson(items: List<RemAusencia>): String =
        items.joinToString(prefix = "[", postfix = "]") { a ->
            buildString {
                append('{')
                append("\"nome\":"); append(jsonStr(a.nome)); append(',')
                append("\"motivo\":"); append(jsonStr(a.motivo)); append(',')
                append("\"inicio\":"); append(jsonStr(a.inicio)); append(',')
                append("\"fim\":"); append(jsonStr(a.fim))
                if (a.horas != null) {
                    append(',')
                    append("\"horas\":")
                    append(a.horas)
                }
                append('}')
            }
        }

    private fun ausenciasDe(o: JsonMap): List<RemAusencia> =
        o.array("ausencias")?.mapNotNull { node ->
            val m = node as? JsonMap ?: return@mapNotNull null
            RemAusencia(m.str("nome"), m.str("motivo"), m.str("inicio"), m.str("fim"), m.double("horas"))
        }.orEmpty()

    private fun stringListToJson(items: List<String>): String =
        items.joinToString(prefix = "[", postfix = "]") { jsonStr(it) }

    private fun breakdownToJson(b: RemOcorrenciasBreakdown): String = buildString {
        append('{')
        fun num(key: String, v: Double?) {
            if (v != null) append("\"$key\":$v,")
        }
        num("comAfastamento", b.comAfastamento)
        num("semAfastamento", b.semAfastamento)
        num("simplesAtendimentoAmbulatorial", b.simplesAtendimentoAmbulatorial)
        num("diasPerdidosNoMes", b.diasPerdidosNoMes)
        num("diasTransportados", b.diasTransportados)
        num("diasDebitados", b.diasDebitados)
        if (endsWith(',')) setLength(length - 1)
        append('}')
    }

    fun payloadFromJson(raw: String): RemMonthPayload {
        val root = runCatching { JsonMap.parse(raw) }.getOrNull() ?: return RemMonthPayload()
        return payloadFromMap(root)
    }

    internal fun payloadFromMap(root: JsonMap): RemMonthPayload {
        val lesao = root.obj("ocorrenciasComLesao")
        val traj = root.obj("ocorrenciasTrajeto")
        val desc = root.str("descricaoEmpresas")
        val empresasRaw = root.strList("empresas")
        val empresas = when {
            empresasRaw.isNotEmpty() -> empresasRaw
            desc.isNotBlank() -> desc.split(';').map { it.trim() }.filter { it.isNotEmpty() }
                .ifEmpty { listOf("") }
            else -> listOf("")
        }
        val nomes = root.strList("nomesColaboradores").ifEmpty { listOf("") }
        val blocsRaw = root.array("empresasBlocos")
            ?.mapNotNull { node ->
                val o = node as? JsonMap ?: return@mapNotNull null
                empresaBlocoFromMap(o)
            }
            .orEmpty()
        val base = RemMonthPayload(
            numeroColaboradores = root.double("numeroColaboradores"),
            hher = root.double("hher"),
            homemHoraTreinamento = root.double("homemHoraTreinamento"),
            horaAbsenteismoMedico = root.double("horaAbsenteismoMedico"),
            numeroCat = root.double("numeroCat"),
            ocorrenciasComLesao = breakdownFrom(lesao),
            ocorrenciasTrajeto = breakdownFrom(traj),
            danosPropriedade = root.double("danosPropriedade"),
            empresas = empresas,
            nomesColaboradores = nomes,
            ausencias = ausenciasDe(root).ifEmpty {
                nomes.filter { it.isNotBlank() }.map { RemAusencia(nome = it) }
            },
            descricaoEmpresas = desc.ifBlank {
                empresas.map { it.trim() }.filter { it.isNotEmpty() }.joinToString("; ")
            },
            empresasBlocos = blocsRaw
        )
        // Garante blocos normalizados persistidos na memoria apos load legado.
        return if (blocsRaw.isNotEmpty()) base
        else base.copy(empresasBlocos = base.empresasBlocosNormalizados())
    }

    private fun empresaBlocoFromMap(o: JsonMap): RemEmpresaBloco {
        val lesao = o.obj("ocorrenciasComLesao")
        val traj = o.obj("ocorrenciasTrajeto")
        return RemEmpresaBloco(
            nome = o.str("nome"),
            numeroColaboradores = o.double("numeroColaboradores"),
            hher = o.double("hher"),
            homemHoraTreinamento = o.double("homemHoraTreinamento"),
            horaAbsenteismoMedico = o.double("horaAbsenteismoMedico"),
            numeroCat = o.double("numeroCat"),
            ocorrenciasComLesao = breakdownFrom(lesao),
            ocorrenciasTrajeto = breakdownFrom(traj),
            danosPropriedade = o.double("danosPropriedade"),
            nomesColaboradores = o.strList("nomesColaboradores").ifEmpty { listOf("") },
            ausencias = ausenciasDe(o).ifEmpty {
                o.strList("nomesColaboradores").filter { it.isNotBlank() }.map { RemAusencia(nome = it) }
            }
        )
    }

    private fun breakdownFrom(node: JsonMap?): RemOcorrenciasBreakdown {
        if (node == null) return RemOcorrenciasBreakdown()
        return RemOcorrenciasBreakdown(
            comAfastamento = node.double("comAfastamento"),
            semAfastamento = node.double("semAfastamento"),
            simplesAtendimentoAmbulatorial = node.double("simplesAtendimentoAmbulatorial"),
            diasPerdidosNoMes = node.double("diasPerdidosNoMes")
                ?: node.double("diasNoMes"),
            diasTransportados = node.double("diasTransportados"),
            diasDebitados = node.double("diasDebitados")
        )
    }

    private fun jsonStr(s: String): String {
        val esc = s.replace("\\", "\\\\").replace("\"", "\\\"")
            .replace("\n", "\\n").replace("\r", "\\r").replace("\t", "\\t")
        return "\"$esc\""
    }
}
